import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, Search, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import AutopayPanel from "@/components/AutopayPanel";
import StatsScopeToggle from "@/components/StatsScopeToggle";
import { scopeRange } from "@/lib/scope";

// Format an ISO timestamp as "YYYY-MM-DD HH:mm" in the user's locale —
// matches the visual pattern used across the Admin Invoices tab so the two
// screens feel consistent.
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const date = d.toISOString().slice(0, 10);
    const h = String(d.getHours()).padStart(2, "0");
    const m = String(d.getMinutes()).padStart(2, "0");
    return `${date} ${h}:${m}`;
  } catch {
    return iso;
  }
};
const fmtDateOnly = (iso) => (iso ? String(iso).slice(0, 10) : "—");
const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

function StatusPill({ status }) {
  const s = (status || "pending").toLowerCase();
  if (s === "paid") {
    return (
      <Badge variant="outline" className="border-green-500/40 bg-green-50 text-green-700" data-testid="invoice-status-paid">
        ✓ Paid
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-amber-500/40 bg-amber-50 text-amber-700" data-testid="invoice-status-pending">
      Pending
    </Badge>
  );
}

export default function PharmacyStaffInvoices() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  // Scope defaults to the current week — most pharmacies pay a weekly
  // invoice so "this week" is the most common view when they open the tab.
  const [scope, setScope] = useState("week");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pharmacy/invoices");
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Apply scope window (filters by `sent_at` — the date the invoice was
  // issued to the pharmacy, which is what "this week" / "last month" means
  // intuitively on this screen). An invoice with no sent_at is kept so a
  // half-populated row doesn't vanish silently.
  const scoped = useMemo(() => {
    const { start, end } = scopeRange(scope);
    if (!start && !end) return rows;
    const startMs = start ? start.getTime() : null;
    const endMs = end ? end.getTime() : null;
    return rows.filter(r => {
      if (!r.sent_at) return true;
      const t = new Date(r.sent_at).getTime();
      if (!Number.isFinite(t)) return true;
      if (startMs !== null && t < startMs) return false;
      if (endMs !== null && t >= endMs) return false;
      return true;
    });
  }, [rows, scope]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter(r =>
      (r.invoice_number || "").toLowerCase().includes(q) ||
      (r.to_email || "").toLowerCase().includes(q) ||
      (r.pharmacy_name || "").toLowerCase().includes(q)
    );
  }, [scoped, filter]);

  // Open the PDF in a new tab. The pharmacy-scoped endpoint accepts an
  // `?auth=` query token (same pattern as the admin PDF route) so the new
  // tab can authenticate without an Authorization header.
  const openPdf = (inv) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = encodeURIComponent(localStorage.getItem("token") || "");
    const url = `${base}/api/pharmacy/invoices/${inv.id}/pdf?auth=${token}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Summary tiles now reflect the scope (totals change as the operator
  // flips between Week / Current Month / Last Month / YTD).
  const totalBilled = useMemo(() => scoped.reduce((s, r) => s + Number(r.total || 0), 0), [scoped]);
  const totalPaid = useMemo(
    () => scoped.filter(r => (r.payment_status || "").toLowerCase() === "paid")
                .reduce((s, r) => s + Number(r.total || 0), 0),
    [scoped]
  );
  const totalPending = Math.max(0, totalBilled - totalPaid);

  return (
    <div className="p-4 lg:p-6 max-w-[1400px] mx-auto" data-testid="pharmacy-invoices-page">
      <div className="flex items-start gap-3 flex-wrap mb-5">
        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <FileText className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">Invoices</h1>
          <p className="text-sm text-muted-foreground">
            Weekly delivery invoices for {user?.pharmacy_name || "your pharmacy"}. Read-only.
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={load} title="Refresh" data-testid="pharmacy-invoices-refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Scope pills — Week (default) / Current Month / Last Month / YTD.
          Filters the invoice list AND the summary tiles below by
          `sent_at`. "Today" is intentionally omitted (invoices are issued
          weekly on Mondays, so a daily view is noise). (2026-10-01) */}
      <div className="mb-4" data-testid="invoices-scope-row">
        <StatsScopeToggle value={scope} onChange={setScope} />
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Card className="p-3" data-testid="summary-total-billed">
          <div className="text-xs text-muted-foreground">Total billed</div>
          <div className="text-xl font-semibold tabular-nums">{fmtMoney(totalBilled)}</div>
        </Card>
        <Card className="p-3" data-testid="summary-total-paid">
          <div className="text-xs text-muted-foreground">Paid</div>
          <div className="text-xl font-semibold tabular-nums text-green-700">{fmtMoney(totalPaid)}</div>
        </Card>
        <Card className="p-3" data-testid="summary-total-pending">
          <div className="text-xs text-muted-foreground">Pending</div>
          <div className="text-xl font-semibold tabular-nums text-amber-700">{fmtMoney(totalPending)}</div>
        </Card>
      </div>

      {/* Main 2-column layout: invoice table on the left, autopay on the right */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6">
        <Card className="overflow-hidden" data-testid="pharmacy-invoices-table-card">
          <div className="p-3 border-b flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder="Search by invoice #, email…"
                className="pl-8 h-9"
                data-testid="pharmacy-invoices-search"
              />
            </div>
            <div className="text-xs text-muted-foreground">
              {filtered.length} of {scoped.length} invoice{scoped.length === 1 ? "" : "s"}
            </div>
          </div>

          {loading && rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Loading invoices…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground" data-testid="pharmacy-invoices-empty">
              {scoped.length === 0
                ? (rows.length === 0
                    ? "No invoices yet. Weekly invoices appear here every Monday."
                    : "No invoices in this period.")
                : "No invoices match your search."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Sent</th>
                    <th className="text-left px-3 py-2 font-medium">Invoice #</th>
                    <th className="text-left px-3 py-2 font-medium">Pharmacy</th>
                    <th className="text-left px-3 py-2 font-medium">Period</th>
                    <th className="text-left px-3 py-2 font-medium">To</th>
                    <th className="text-right px-3 py-2 font-medium">Rows</th>
                    <th className="text-right px-3 py-2 font-medium">Total</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-right px-3 py-2 font-medium w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((inv) => (
                    <tr
                      key={inv.id}
                      className="border-t hover:bg-secondary/20"
                      data-testid={`pharmacy-invoice-row-${inv.invoice_number || inv.id}`}
                    >
                      <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{fmtDateTime(inv.sent_at)}</td>
                      <td className="px-3 py-3 font-medium whitespace-nowrap">{inv.invoice_number || "—"}</td>
                      <td className="px-3 py-3">{inv.pharmacy_name || user?.pharmacy_name || "—"}</td>
                      <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">
                        {fmtDateOnly(inv.period_from)} <span className="opacity-50">→</span>{" "}
                        {fmtDateOnly(inv.period_to)}
                      </td>
                      <td className="px-3 py-3 text-muted-foreground truncate max-w-[220px]" title={inv.to_email}>
                        {inv.to_email || "—"}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">{inv.rows ?? "—"}</td>
                      <td className="px-3 py-3 text-right font-semibold tabular-nums">{fmtMoney(inv.total)}</td>
                      <td className="px-3 py-3"><StatusPill status={inv.payment_status} /></td>
                      <td className="px-3 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openPdf(inv)}
                          title="View PDF"
                          data-testid={`pharmacy-invoice-view-${inv.invoice_number || inv.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Autopay panel — pharmacy self-serve. Moved here from the Overview
            tab per 2026-10-01 request so billing-related things live under
            the Invoices tab. */}
        <div data-testid="pharmacy-invoices-autopay-column">
          {user?.pharmacy_id && (
            <AutopayPanel
              pharmacyId={user.pharmacy_id}
              pharmacyName={user?.pharmacy_name || "your pharmacy"}
              onChange={load}
            />
          )}
        </div>
      </div>
    </div>
  );
}
